// Shared ECU checksum detection + correction (reused by Checksum Calculator).
// Types reverse-engineered from ChecksumCalculator.exe, verified byte-for-byte.
const TYPES = {
  honda_rh850: { label: "Honda / Denso RH850 (32-bit)", width: 4, endian: "big", defAddr: 0x5effc, regionStart: 0x4f000, mcu: "Renesas RH850", ecuMaker: "Honda (Denso)" },
  shindengen_rh850: { label: "Honda / Shindengen RH(V)850 (32-bit)", width: 4, endian: "little", wholeFile: true, defAddr: 0x1afe0, mcu: "Renesas RH(V)850", ecuMaker: "Shindengen" },
  kawasaki_denso16: { label: "Kawasaki / Denso 16-bit (CVN)", width: 2, endian: "big", wholeFile: true, defAddr: 0x7c28, mcu: "Renesas SH7058 (Denso)", ecuMaker: "Kawasaki (Denso)" },
  honda_8bit: { label: "Honda / Keihin 8-bit", width: 1, endian: "n/a", defAddr: 0x0, mcu: "Keihin PGM-FI", ecuMaker: "Honda (Keihin)" },
};

// Honda/Keihin 8-bit checksum byte location varies by ROM size (verified: 48K@0x9FEF, 56K@0xDFEF, 1MB@0xFFFF8).
function honda8bitAddr(size) {
  if (size === 0x100000) return 0xFFFF8;
  if (size === 0x20000) return 0x1000F; // Keihin M16C 128K (KWT-712) — verified vs ChecksumCalculator
  if (size === 0x18000 || size === 0x10000) return 0x0; // Keihin 64K/96K — 8-bit sum-to-zero, checksum byte @0x0 (matches flash path)
  if (size === 0xE000) return 0xDFEF;
  if (size === 0xC000) return 0x9FEF;
  return 0x0;
}

// Shindengen (RH850/V850) 384KB checksum slot is DYNAMIC — NOT a fixed address.
// Reference algorithm (ShindengenAutoChecksum): find the write-key signature, then the
// first 0xD0 0x07 marker after it; the 4-byte checksum slot = markerPos - 4 (with a mod-4
// align tweak). Value = whole-file little-endian 32-bit sum ≡ 0. Verified byte-for-byte on
// KZRM-B21 (0x1AFE0), KZRM-B31 (0x1AFE8), KZRA-601 (0x1AFE0), K59J (0x1BF70).
const SH_WKEY_SIG = [0x11, 0x12, 0x13, 0x14, 0x15, 0x15, 0x17];
function indexOfSeq(u8, sig, from = 0) {
  outer: for (let i = from; i <= u8.byteLength - sig.length; i++) {
    for (let k = 0; k < sig.length; k++) if (u8[i + k] !== sig[k]) continue outer;
    return i;
  }
  return -1;
}
// Returns the dynamic Shindengen checksum slot, or null if this is not a Shindengen image
// (the write-key signature is the reliable Shindengen fingerprint — Denso 384KB has none).
export function shindengenChecksumAddr(u8) {
  const i = indexOfSeq(u8, SH_WKEY_SIG);
  if (i < 0) return null;
  for (let j = i + SH_WKEY_SIG.length; j < u8.byteLength - 6; j++) {
    if (u8[j] === 0xD0 && u8[j + 1] === 0x07) {
      let num = j;
      if ((j - ((j - 4) & ~3)) === 6) num = j - 6;
      return num - 4;
    }
  }
  return null;
}

function compute(u8, typeKey, addr) {
  const t = TYPES[typeKey];
  const view = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
  if (addr < 0 || addr + t.width > u8.byteLength) return { error: "Checksum address out of range." };
  if (typeKey === "honda_8bit") {
    let rest = 0;
    for (let i = 0; i < u8.byteLength; i++) { if (i === addr) continue; rest = (rest + u8[i]) & 0xff; }
    const stored = u8[addr]; const corrected = (0 - rest) & 0xff;
    return { stored, corrected, valid: stored === corrected, sum: rest, width: 1 };
  }
  if (typeKey === "honda_rh850") {
    let sum = 0;
    for (let o = t.regionStart; o < addr; o += 4) sum = (sum + view.getUint32(o, false)) >>> 0;
    const stored = view.getUint32(addr, false); const corrected = (0 - sum) >>> 0;
    return { stored, corrected, valid: stored === corrected, sum, width: 4 };
  }
  if (typeKey === "shindengen_rh850") {
    let sum = 0;
    for (let o = 0; o + 4 <= u8.byteLength; o += 4) { if (o === addr) continue; sum = (sum + view.getUint32(o, true)) >>> 0; }
    const stored = view.getUint32(addr, true); const corrected = (0 - sum) >>> 0;
    return { stored, corrected, valid: stored === corrected, sum, width: 4 };
  }
  // kawasaki_denso16
  if (addr + 4 > u8.byteLength) return { error: "Checksum block out of range." };
  let B = 0;
  for (let o = 0; o + 2 <= u8.byteLength; o += 2) { if (o === addr || o === addr + 2) continue; B = (B + view.getUint16(o, false)) & 0xffff; }
  const correctedC = (0x10000 - B) & 0xffff; const corrected = (~correctedC) & 0xffff;
  const storedW = view.getUint16(addr, false); const storedC = view.getUint16(addr + 2, false);
  return { stored: storedW, corrected, correctedC, storedC, valid: storedW === corrected && storedC === correctedC, sum: B, width: 2, kawa: true };
}

function patch(u8, typeKey, result) {
  const out = new Uint8Array(u8);
  const t = TYPES[typeKey]; const addr = result.addr, corrected = result.corrected;
  if (typeKey === "kawasaki_denso16") {
    out[addr] = (corrected >>> 8) & 0xff; out[addr + 1] = corrected & 0xff;
    out[addr + 2] = (result.correctedC >>> 8) & 0xff; out[addr + 3] = result.correctedC & 0xff;
    return out;
  }
  if (t.width === 1) out[addr] = corrected & 0xff;
  else if (t.width === 4 && t.endian === "big") { out[addr] = (corrected >>> 24) & 0xff; out[addr + 1] = (corrected >>> 16) & 0xff; out[addr + 2] = (corrected >>> 8) & 0xff; out[addr + 3] = corrected & 0xff; }
  else if (t.width === 4) { out[addr] = corrected & 0xff; out[addr + 1] = (corrected >>> 8) & 0xff; out[addr + 2] = (corrected >>> 16) & 0xff; out[addr + 3] = (corrected >>> 24) & 0xff; }
  else { out[addr] = corrected & 0xff; out[addr + 1] = (corrected >>> 8) & 0xff; }
  return out;
}

function autoDetect(u8) {
  const order = ["kawasaki_denso16", "shindengen_rh850", "honda_rh850", "honda_8bit"];
  const cands = [];
  for (const key of order) {
    const t = TYPES[key]; let addr = key === "honda_8bit" ? honda8bitAddr(u8.byteLength) : t.defAddr;
    if (key === "shindengen_rh850" && u8.byteLength === 0x40000) addr = shindengenChecksumAddr(u8) ?? 0x3FFF8; // Shindengen 256K dynamic slot (K2SA @0x3DA60), else Keihin 256K
    if (addr == null || addr + t.width > u8.byteLength) continue;
    const r = compute(u8, key, addr);
    if (r && !r.error) cands.push({ key, addr, valid: !!r.valid });
  }
  return cands;
}

// Keihin 256KB K2F (RH850): Honda 8-bit whole-file sum-to-zero @0x9FEF (matches the device-confirmed flash path).
const isK2F256 = (u8, hint) => u8 && u8.byteLength === 0x40000 && shindengenChecksumAddr(u8) == null && (hint === 0x9FEF || looksLikeK2F256(u8));

// Keihin K2F 256KB (RH850) without ECM ID: no Shindengen signature and "K2F" text in the image (e.g. K2FA104K).
export function looksLikeK2F256(u8) {
  if (!u8 || u8.byteLength !== 0x40000 || shindengenChecksumAddr(u8) != null) return false;
  for (let i = 0; i + 3 <= u8.byteLength; i++) if (u8[i] === 0x4b && u8[i + 1] === 0x32 && u8[i + 2] === 0x46) return true;
  return false;
}

// Detect + correct a bin's checksum. Only modifies when confident about the type.
// hint = checksum slot from the ECM DB / UI (used to disambiguate Keihin 256KB K2F).
export function autoCorrectChecksum(u8, hint) {
  // Denso 96KB K99-B02 (CRF250F): XTune preparation (block 0x3C80) + Σ16 BE [0,0x13C00) == 0xAA55 @0x13BFE.
  if (u8 && u8.byteLength === 0x18000) {
    const out = densoK99Prepare(u8);
    const s0 = denso96Sum(out);
    const oldW = (out[0x13bfe] << 8) | out[0x13bff];
    const changed = s0 !== 0xaa55 || out !== u8;
    const w = (oldW + 0xaa55 - s0) & 0xffff;
    if (s0 !== 0xaa55) { out[0x13bfe] = (w >> 8) & 0xff; out[0x13bff] = w & 0xff; }
    const type = { label: "Denso 96KB Σ16 (K99)", ecuMaker: "Honda (Denso)" };
    return { ok: true, out, changed, already: !changed, type, addr: 0x13BFE, corrected: w };
  }
  if (isK2F256(u8, hint)) {
    const r = compute(u8, "honda_8bit", 0x9FEF);
    if (r.valid) return { ok: true, out: u8, changed: false, already: true, type: TYPES.honda_8bit, addr: 0x9FEF, corrected: r.corrected };
    return { ok: true, out: patch(u8, "honda_8bit", { ...r, addr: 0x9FEF }), changed: true, type: TYPES.honda_8bit, addr: 0x9FEF, corrected: r.corrected };
  }
  // Keihin M16C 128K (KWT-712): Honda 8-bit whole-file sum, checksum byte @0x1000F.
  // Verified byte-for-byte vs ChecksumCalculator (INVICTA 128 TEST 2 → only 0x1000F changes).
  if (u8 && u8.byteLength === 0x20000) {
    const r = compute(u8, "honda_8bit", 0x1000F);
    if (r.error) return { ok: false, error: r.error };
    if (r.valid) return { ok: true, out: u8, changed: false, already: true, type: TYPES.honda_8bit, addr: 0x1000F, corrected: r.corrected };
    const out = patch(u8, "honda_8bit", { ...r, addr: 0x1000F });
    return { ok: true, out, changed: true, type: TYPES.honda_8bit, addr: 0x1000F, corrected: r.corrected };
  }
  // Honda 384KB (0x60000): Shindengen (dynamic slot, whole-file LE32 sum≡0) if the write-key
  // signature is present; otherwise Denso honda_rh850 @0x5EFFC. This replaces the old fixed
  // 0x1AFE0 guess, which corrupted edited files whose real slot differs (e.g. B31=0x1AFE8).
  if (u8 && (u8.byteLength === 0x60000 || (u8.byteLength === 0x40000 && shindengenChecksumAddr(u8) != null))) {
    const shAddr = shindengenChecksumAddr(u8);
    const sel = shAddr != null ? { key: "shindengen_rh850", addr: shAddr } : { key: "honda_rh850", addr: 0x5effc };
    const r = compute(u8, sel.key, sel.addr);
    if (r.error) return { ok: false, error: r.error };
    const t = TYPES[sel.key];
    if (r.valid) return { ok: true, out: u8, changed: false, already: true, type: t, addr: sel.addr, corrected: r.corrected };
    const out = patch(u8, sel.key, { ...r, addr: sel.addr });
    return { ok: true, out, changed: true, type: t, addr: sel.addr, corrected: r.corrected };
  }
  const cands = autoDetect(u8);
  let chosen = cands.find((c) => c.valid) || null;
  const already = !!chosen;
  if (!chosen) {
    if (u8.byteLength === 0x80000) chosen = { key: "kawasaki_denso16", addr: 0x7c28 };
    else if (u8.byteLength === 0x60000) chosen = u8[1] === 0x02 ? { key: "shindengen_rh850", addr: 0x1afe0 } : { key: "honda_rh850", addr: 0x5effc };
    else if (u8.byteLength === 0x40000) chosen = { key: "honda_8bit", addr: 0x3FFF8 }; // non-Shindengen 256K = Keihin (K93)
    else if (u8.byteLength === 0x100000) chosen = { key: "honda_8bit", addr: 0xFFFF8 };
    else if (u8.byteLength === 0xE000) chosen = { key: "honda_8bit", addr: 0xDFEF };
    else if (u8.byteLength === 0xC000) chosen = { key: "honda_8bit", addr: 0x9FEF };
    else if (u8.byteLength === 0x10000 || u8.byteLength === 0x18000) chosen = { key: "honda_8bit", addr: 0x0 }; // Keihin 64K/96K
    else return { ok: true, out: u8, changed: false, unknown: true };
  }
  const r = compute(u8, chosen.key, chosen.addr);
  if (r.error) return { ok: false, error: r.error };
  const t = TYPES[chosen.key];
  if (r.valid) return { ok: true, out: u8, changed: false, already, type: t, addr: chosen.addr, corrected: r.corrected };
  const out = patch(u8, chosen.key, { ...r, addr: chosen.addr });
  return { ok: true, out, changed: true, type: t, addr: chosen.addr, corrected: r.corrected };
}

// Non-mutating status for a live badge: valid / invalid / unknown.
export function getChecksumStatus(u8, hint) {
  if (!u8 || !u8.byteLength) return { state: "unknown", label: "No file" };
  if (u8.byteLength === 0x18000) {
    const ok = denso96Sum(densoK99Prepare(u8)) === 0xaa55;
    return { state: ok ? "valid" : "invalid", label: `Denso 96KB Σ16 checksum ${ok ? "OK" : "invalid"}`, addr: 0x13BFE };
  }
  if (isK2F256(u8, hint)) {
    const r = compute(u8, "honda_8bit", 0x9FEF);
    return { state: r.valid ? "valid" : "invalid", label: `Keihin K2F 8-bit checksum ${r.valid ? "OK" : "invalid"}`, addr: 0x9FEF };
  }
  if (u8.byteLength === 0x20000) {
    const r = compute(u8, "honda_8bit", 0x1000F);
    if (!r.valid && denso128Sum(u8) === 0xaa55) return { state: "valid", label: "Denso 128KB Σ16 checksum OK", addr: 0x1BBFE };
    return r.valid
      ? { state: "valid", label: "Honda / Keihin 8-bit checksum OK", addr: 0x1000F }
      : { state: "invalid", label: "Honda / Keihin 8-bit checksum invalid", addr: 0x1000F };
  }
  if (u8.byteLength === 0x60000 || (u8.byteLength === 0x40000 && shindengenChecksumAddr(u8) != null)) {
    const shAddr = shindengenChecksumAddr(u8);
    const sel = shAddr != null ? { key: "shindengen_rh850", addr: shAddr } : { key: "honda_rh850", addr: 0x5effc };
    const r = compute(u8, sel.key, sel.addr);
    const t = TYPES[sel.key];
    return r.valid
      ? { state: "valid", label: `${t.ecuMaker} checksum OK`, addr: sel.addr }
      : { state: "invalid", label: `${t.ecuMaker} checksum invalid`, addr: sel.addr };
  }
  const cands = autoDetect(u8);
  const valid = cands.find((c) => c.valid);
  if (valid) { const t = TYPES[valid.key]; return { state: "valid", label: `${t.ecuMaker} checksum OK`, addr: valid.addr }; }
  let key = null, addr = null;
  if (u8.byteLength === 0x80000) { key = "kawasaki_denso16"; addr = 0x7c28; }
  else if (u8.byteLength === 0x60000) { key = u8[1] === 0x02 ? "shindengen_rh850" : "honda_rh850"; addr = key === "shindengen_rh850" ? 0x1afe0 : 0x5effc; }
  else if (u8.byteLength === 0x40000) { key = "honda_8bit"; addr = 0x3FFF8; } // non-Shindengen 256K = Keihin (K93)
  else if (u8.byteLength === 0x100000) { key = "honda_8bit"; addr = 0xFFFF8; }
  else if (u8.byteLength === 0xE000) { key = "honda_8bit"; addr = 0xDFEF; }
  else if (u8.byteLength === 0xC000) { key = "honda_8bit"; addr = 0x9FEF; }
  else if (u8.byteLength === 0x10000 || u8.byteLength === 0x18000) { key = "honda_8bit"; addr = 0x0; } // Keihin 64K/96K
  else if (cands.length) { key = cands[0].key; addr = cands[0].addr; }
  if (!key) return { state: "unknown", label: "Checksum type unknown" };
  return { state: "invalid", label: `${TYPES[key].ecuMaker} checksum invalid`, addr };
}

// Denso 128KB (LegacyDenso): Σ BE16 over [0x0, 0x1BC00) must be 0xAA55 (correction word @0x1BBFE).
function denso128Sum(u8) { let s = 0; for (let o = 0; o < 0x1bc00; o += 2) s = (s + ((u8[o] << 8) | u8[o + 1])) & 0xffff; return s; }
// Denso 96KB K99-B02: Σ BE16 over [0x0, 0x13C00) must be 0xAA55 (word @0x13BFE) — HW-proven 2026-10-19.
export function denso96Sum(u8) { let s = 0; for (let o = 0; o < 0x13c00; o += 2) s = (s + ((u8[o] << 8) | u8[o + 1])) & 0xffff; return s; }
// XTune K99 preparation: the stock block @0x3C80 is replaced by a reference block before write (the stock
// image only sums to AA55 after this). Returns a copy when replaced, the same array otherwise.
const K99_STOCK_3C80 = "4c31800c4de4230024bdd366814da97aab8027caec97da0aa16d89fa0b32f69489c1bfa21804c1c51360b90361f2b6feb5e0b1f6b2052393ce5fb4e3805549964e14d9c7a8780b3a1ee81924526ea5c1c24521c5aa5d804dcf6957e9d68de7eb11f30cc05a2aa8cd58f87ab1bcf07d9a87e9b5ff502d4e3964b0e92bcb5cea5f";
const K99_PREP_3C80 = "4a8000fbb63cb4406a000afc769efd76a0f33d34137c3e0cfc3d34fe76a227021810f33e0c7c3d360a1f3c9940161f321d01111e321d020c1d321d011d3c99401c3c99801e3c9980041820008f793ca81e3c1302f414101c0250081d0258011d024040dc44c301005c561e024108061d02400820041c024008875a485a495a4a";
export function densoK99Prepare(u8) {
  const cur = Array.from(u8.slice(0x3c80, 0x3d00)).map((b) => b.toString(16).padStart(2, "0")).join("");
  if (cur !== K99_STOCK_3C80) return u8;
  const out = new Uint8Array(u8);
  for (let j = 0; j < 128; j++) out[0x3c80 + j] = parseInt(K99_PREP_3C80.substr(j * 2, 2), 16);
  return out;
}
// Denso 384KB integrity window: Σ BE32 over [0x10000, 0x5FE00) must be 0xAA5555AA (word @0x5EFFC).
function denso384Win(u8) {
  const v = new DataView(u8.buffer, u8.byteOffset, u8.byteLength); let s = 0;
  for (let o = 0x10000; o < 0x5fe00; o += 4) s = (s + v.getUint32(o, false)) >>> 0;
  return s;
}

// Every checksum check that applies to this file: [{ name, addr, valid }].
export function listChecksums(u8, hint) {
  if (!u8 || !u8.byteLength) return [];
  const n = u8.byteLength;
  if (n === 0x18000) return [{ name: "Denso 96KB Σ16 (K99)", addr: 0x13BFE, valid: denso96Sum(densoK99Prepare(u8)) === 0xaa55 }];
  if (n === 0x20000) {
    const k = compute(u8, "honda_8bit", 0x1000F).valid, d = denso128Sum(u8) === 0xaa55;
    if (k) return [{ name: "Keihin 8-bit", addr: 0x1000F, valid: true }];
    if (d) return [{ name: "Denso 128KB Σ16", addr: 0x1BBFE, valid: true }];
    return [{ name: "Keihin 8-bit", addr: 0x1000F, valid: false }, { name: "Denso 128KB Σ16", addr: 0x1BBFE, valid: false }];
  }
  if (n === 0x60000 && shindengenChecksumAddr(u8) == null) {
    return [
      { name: "Denso RH850 region", addr: 0x5effc, valid: !!compute(u8, "honda_rh850", 0x5effc).valid },
      { name: "Denso integrity window", addr: 0x5effc, valid: denso384Win(u8) === 0xaa5555aa },
    ];
  }
  const st = getChecksumStatus(u8, hint);
  if (st.addr == null) return [];
  return [{ name: st.label.replace(/ checksum.*$/i, ""), addr: st.addr, valid: st.state === "valid" }];
}
